import { BaseEntity } from '@ianesthesie-kit/services';
import { ExtendedRisk, Risk, SourceType } from '@/modules/surgicalEvent/model';
import { EntityUserInputWrapper } from '@/modules/surgicalEvent/util';

/**
 * The risk field names, the one place they are listed: the `AnesthesiaRisk` fields derive from it. Keys of
 * `AnesthesiaRisk.riskSources` and of the site "risks shown" list. Mirrors the backend entity's risk fields.
 */
export const ANESTHESIA_RISK_NAMES = [
  'intubation',
  'ventilation',
  'aspiration',
  'hypotension',
  'hemorrhage',
  'postOpPain',
  'ponv',
  'delirium',
  'phlebitis',
  'dental',
  'transfusion',
  'kidney',
  'allergy',
  'cardiac',
  'hepatic',
  'infection'
] as const;

export type AnesthesiaRiskName = (typeof ANESTHESIA_RISK_NAMES)[number];

/** One optional level per risk name; hemorrhage has its own wider scale. */
type AnesthesiaRiskLevels = {
  [K in Exclude<AnesthesiaRiskName, 'hemorrhage'>]?: Risk;
} & { hemorrhage?: ExtendedRisk };

export interface AnesthesiaRisk extends BaseEntity, AnesthesiaRiskLevels {
  /**
   * Who set each level: DOCTOR_INPUT, PREDEFINED (surgery catalog) or AUTOMATION. A risk without an entry has no
   * known source (set before the field existed). Show the "automatic" marker only when the source is present and
   * not DOCTOR_INPUT. Read-only: the backend sets it.
   */
  riskSources?: Partial<Record<AnesthesiaRiskName, SourceType>>;
}

export interface AnesthesiaType extends BaseEntity {
  code: string;
  translationCode: string;
}

export interface AntibioticProphylaxisEntry {
  value: string;
  /** Set by the backend; echoed entries keep their source and new entries are doctor input. */
  sourceType?: SourceType;
}

export type PlannedAnesthesiaType = EntityUserInputWrapper<AnesthesiaType> & {
  sourceType?: SourceType;
};

export interface AnesthesiaInfo extends BaseEntity {
  plannedAnesthesiaTypes?: AnesthesiaType[];
  userInputPlannedAnesthesiaTypes?: string[];
  combinedOrderedPlannedAnesthesiaTypes: PlannedAnesthesiaType[];
  antibioticProphylaxisEntries: AntibioticProphylaxisEntry[];
  /** Specific monitoring badges (site list and free text), in doctor order. */
  specificMonitoring: string[];
  /** Additional-protocol badges (site list and free text), in doctor order. Not care-pathway protocols. */
  additionalAnesthesiaProtocols: string[];
  /** Planned types the event's surgeries suggest, computed by the backend on the single read. Read-only. */
  suggestedPlannedAnesthesiaTypes?: AnesthesiaType[];
  gaAirwayType?: string;
  ponvPrevention?: string;
  preopVentilation?: string;
  currentCovid?: string;
  lastPcrDate?: string;
  mobilityComment?: string;
  psychiatricComment?: string;
  counterIndication?: string;
  comment?: string;
}
